/** A rating as the API sends it for films and series (OMDb sources and the TMDb vote). */
export interface MetadataRating {
  source: string;
  value: string | number;
  max?: number;
  votes?: number;
}

export interface RatingChip {
  key: string;
  label: string;
  score: string;
  votes: string;
}

const ORDER = ['imdb', 'rotten_tomatoes', 'metacritic', 'tmdb'];
const META: Record<string, { label: string; max: number | null }> = {
  imdb: { label: 'IMDb', max: 10 },
  rotten_tomatoes: { label: 'Rotten Tomatoes', max: null },
  metacritic: { label: 'Metacritic', max: 100 },
  tmdb: { label: 'TMDb', max: 10 },
};

function sourceKey(source: string): string {
  return String(source || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
}

function score(key: string, value: string | number, max?: number): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (raw.includes('%') || raw.includes('/')) return raw;
  const n = Number(raw.replace(',', '.'));
  if (!Number.isFinite(n)) return raw;
  if (key === 'rotten_tomatoes') return `${Math.round(n)}%`;
  const scale = Number(max) > 0 ? Number(max) : META[key]?.max ?? null;
  if (scale === 10) return `${(Math.round(n * 10) / 10).toFixed(1)}/10`;
  if (scale === 100) return `${Math.round(n)}/100`;
  return scale ? `${n}/${scale}` : raw;
}

export function compactVotes(votes: unknown): string {
  const n = Number(votes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `${m >= 10 ? Math.round(m) : m.toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (n >= 1_000) {
    const k = n / 1_000;
    return `${k >= 10 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, '')}K`;
  }
  return String(Math.round(n));
}

/** Chips in a fixed source order (the order Tesla FE shows), one per source. */
export function ratingChips(ratings: unknown): RatingChip[] {
  const bySource = new Map<string, RatingChip>();
  for (const item of Array.isArray(ratings) ? ratings as MetadataRating[] : []) {
    if (!item || typeof item !== 'object') continue;
    const key = sourceKey(item.source);
    const text = key ? score(key, item.value, item.max) : '';
    if (!text) continue;
    bySource.set(key, { key, label: META[key]?.label ?? item.source, score: text, votes: compactVotes(item.votes) });
  }
  const ordered = ORDER.flatMap((key) => (bySource.has(key) ? [bySource.get(key)!] : []));
  for (const [key, chip] of bySource) if (!ORDER.includes(key)) ordered.push(chip);
  return ordered;
}
